/**
 * Project domain form schema (entity layer).
 *
 * Hoisted from `features/create-project/model/schema.ts` to eliminate the
 * cross-feature value import that `features/edit-project/model/schema.ts`
 * had on the create-project schema.
 *
 * Both feature slices (create-project + edit-project) project the same
 * Project entity shape into a form, so the schema describes the ENTITY,
 * not a feature workflow. Features may not value-import from each other, so
 * the shared shape belongs in the entity layer.
 *
 * What stays in `features/create-project/model/schema.ts`:
 *   - WIZARD_STEPS / WIZARD_STEP_ARRAY (wizard-specific step metadata)
 *   - PersonaOption / mapPersonasToOptions (Persona-domain bridge into
 *     audienceIds form options — kept in create-project/model because both
 *     create + edit need it but Persona is not a Project entity)
 *
 * What this file owns:
 *   - buildCreateProjectSchema (Zod)
 *   - CreateProjectFormData (inferred type)
 *   - defaultFormData (RHF defaults)
 *   - REQUIRED_FIELDS (driven by the wizard step metadata)
 *   - isFieldRequired (helper bound to REQUIRED_FIELDS)
 *   - MARKET_OPTIONS / PROJECT_TYPE_OPTIONS / STATUS_OPTIONS (UI option
 *     arrays derived from the entity constant maps)
 *
 * NOTE on REQUIRED_FIELDS: the canonical required-field list is the union
 * of the create-project wizard's step requirements ('type', 'title',
 * 'description', 'markets'). The edit-project modal uses the same list
 * because every required field on the create wizard remains required on
 * edit. If that ever diverges, split into per-flow lists exposed by each
 * feature slice.
 */
import { z } from 'zod';
import type { TFunction } from 'i18next';
import { i18n } from '@/shared/i18n';
import { createIsFieldRequired } from '@/shared/utils/form.utils';
import { PROJECT_STATUS_MAP, PROJECT_TYPE_MAP } from './constants';
import type { ProjectStatus, ProjectType } from './types';

const PROJECT_TYPES = Object.keys(PROJECT_TYPE_MAP) as [ProjectType, ...ProjectType[]];
const PROJECT_STATUSES = Object.keys(PROJECT_STATUS_MAP) as [ProjectStatus, ...ProjectStatus[]];

export const TITLE_MAX_LENGTH = 255;
export const DESCRIPTION_MAX_LENGTH = 400;

// A factory, not a module-scope const: building the schema at import time would bake
// in whatever language was active then, and the messages could never follow a change.
export const buildCreateProjectSchema = (t: TFunction<'validation'>) =>
  z
    .object({
      type: z.enum(PROJECT_TYPES, {
        error: t('project.projectForm.projectTypeIsRequired'),
      }),
      title: z
        .string()
        .min(1, t('project.projectForm.titleIsRequired'))
        .max(TITLE_MAX_LENGTH, t('project.projectForm.titleMaxLength', { max: TITLE_MAX_LENGTH })),
      description: z
        .string()
        .min(1, t('project.projectForm.descriptionIsRequired'))
        .max(
          DESCRIPTION_MAX_LENGTH,
          t('project.projectForm.descriptionMaxLength', { max: DESCRIPTION_MAX_LENGTH }),
        ),
      tags: z.array(z.string().trim().min(1).max(80)).optional(),
      markets: z.array(z.string()).min(1, t('project.projectForm.marketIsRequired')),
      status: z.enum(PROJECT_STATUSES).optional(),
      startDate: z.string().optional(),
      deadline: z.string().optional(),
      audienceIds: z.array(z.string()).optional(),
    })
    .refine(
      (data) =>
        !data.startDate || !data.deadline || new Date(data.startDate) <= new Date(data.deadline),
      { message: t('project.projectForm.startDateBeforeDeadline'), path: ['startDate'] },
    );

export type CreateProjectFormData = z.infer<ReturnType<typeof buildCreateProjectSchema>>;

export const defaultFormData = {
  /** Empty string for RHF controlled component (undefined is discouraged by RHF docs). Zod catches it as invalid enum. */
  type: '' as CreateProjectFormData['type'],
  title: '',
  description: '',
  tags: [],
  markets: [],
  status: 'draft',
  startDate: undefined,
  deadline: undefined,
  audienceIds: [],
} as const satisfies CreateProjectFormData;

/**
 * Required fields shared between create + edit flows. Mirrors the
 * create-project wizard step metadata (intake step has no required fields;
 * details step requires type, title, description, markets).
 */
export const REQUIRED_FIELDS: (keyof CreateProjectFormData)[] = [
  'type',
  'title',
  'description',
  'markets',
];

export const isFieldRequired = createIsFieldRequired(REQUIRED_FIELDS);

// Form field options derived from the entity constant maps.

// `label` stays a getter rather than a destructured value: destructuring would
// read the map's localised label at import time and freeze it.
export const PROJECT_TYPE_OPTIONS = (Object.keys(PROJECT_TYPE_MAP) as ProjectType[]).map(
  (value) => ({
    value,
    get label() {
      return PROJECT_TYPE_MAP[value];
    },
  }),
);

// `label` stays a getter rather than a destructured value: destructuring would
// read the entry's localised label at import time and freeze it.
export const STATUS_OPTIONS = Object.entries(PROJECT_STATUS_MAP).map(([value, entry]) => ({
  value,
  get label() {
    return entry.label;
  },
}));

/* eslint-disable i18next/no-literal-string -- persisted market values, not copy */
/**
 * Persisted as `project.markets` and parsed back off the wire by
 * `prefillProjectFromDocuments`, so the strings are the contract; `getMarketLabel`
 * carries the copy.
 */
export const MARKET_OPTIONS = [
  'APAC',
  'ANZ',
  'LATAM',
  'EMEA',
  'North America',
  'Worldwide',
] as const;
/* eslint-enable i18next/no-literal-string */

// The four region acronyms read the same in every locale, so they are their own label.
const MARKET_LABEL_KEY = {
  'North America': 'projects:project.formSchema.northAmerica',
  Worldwide: 'projects:project.formSchema.worldwide',
} as const satisfies Partial<Record<(typeof MARKET_OPTIONS)[number], string>>;

export function getMarketLabel(market: string): string {
  return market in MARKET_LABEL_KEY
    ? i18n.t(MARKET_LABEL_KEY[market as keyof typeof MARKET_LABEL_KEY])
    : market;
}
